/**
 * Inkooptarieven voor bewerkingen en eenmalige instelkosten.
 *
 * **Waarom dit bestaat.** Dit zijn bedragen die Timmers óns rekent. Ze stonden als vaste getallen
 * in de code, en dat is precies hoe de warm-edge-toeslag (€ 2,00 i.p.v. € 2,75) en de
 * energietoeslag (3,7 i.p.v. 10,5 ct/kg) zeven maanden konden verouderen: ze komen niet uit de
 * API en niet uit de prijslijst-Excel, dus geen enkele sync raakte ze aan. Pas op de factuur van
 * Timmers viel het op.
 *
 * Als instelling kun je een gewijzigd tarief bijwerken zonder nieuwe versie van de site.
 *
 * **De marge blijft ongemoeid.** Bewerkingen volgen dezelfde marge als het glas waar ze op zitten
 * (`opsMarginFactor` in de prijsengine, gevuld met dezelfde `catalogFactor` als de m²-prijs). Hier
 * staat dus alleen wat wij inkopen; de verkoopprijs schuift automatisch mee.
 */

export interface BewerkingsTarieven {
  /** Boring: prijs per mm diameter (enkel glas). */
  boringPerMmCents: number;
  /** Boring: minimum per gat, ongeacht diameter. */
  boringMinCents: number;
  /** Slijpwerk op een vormruit: minimumbedrag per ruit. */
  slijpModelMinCents: number;
  /** Idem voor gelaagd glas — daar ligt het minimum hoger. */
  slijpModelMinGelaagdCents: number;
  /** Geëmailleerd glas: eenmalige instelkost per RAL-kleur, standaardkleur. */
  emailInstelkostStandaardCents: number;
  /** Idem voor een niet-standaard RAL. */
  emailInstelkostNietStandaardCents: number;
  /** BENGglas: prijs van een monster/staal. */
  bengSampleCents: number;
  /** BENGglas: hybride opbouw met een gelaagde tegenruit (2× EVA), per m². */
  bengHybrideGelaagdCents: number;
}

/**
 * De tarieven zoals ze op 03-09-2026 in de prijslijsten stonden. Dit is de terugval wanneer de
 * admin (nog) niets heeft ingevuld — bewust de laatst bekende juiste bedragen en niet 0, want 0
 * zou de bewerking gratis maken en dat merk je pas op de factuur van Timmers.
 */
export const DEFAULT_BEWERKINGSTARIEVEN: BewerkingsTarieven = {
  boringPerMmCents: 41,
  boringMinCents: 470,
  slijpModelMinCents: 3000,
  slijpModelMinGelaagdCents: 4000,
  emailInstelkostStandaardCents: 12500,
  emailInstelkostNietStandaardCents: 13500,
  bengSampleCents: 6200,
  bengHybrideGelaagdCents: 6700,
};

/** Velden + labels voor het instellingenscherm; één bron, zodat de admin niets kan missen. */
export const BEWERKINGSTARIEF_VELDEN: { key: keyof BewerkingsTarieven; label: string; help: string; perMm?: boolean }[] = [
  { key: "boringPerMmCents", label: "Boring per mm", help: "Prijs per millimeter gatdiameter (enkel glas).", perMm: true },
  { key: "boringMinCents", label: "Boring minimum", help: "Minimumbedrag per gat, ook bij een kleine diameter." },
  { key: "slijpModelMinCents", label: "Slijpwerk vormruit minimum", help: "Minimum per ruit bij slijpwerk op een vorm." },
  { key: "slijpModelMinGelaagdCents", label: "Slijpwerk vormruit minimum (gelaagd)", help: "Idem voor gelaagd glas." },
  { key: "emailInstelkostStandaardCents", label: "Instelkost geëmailleerd (standaard RAL)", help: "Eenmalig per kleur per order." },
  { key: "emailInstelkostNietStandaardCents", label: "Instelkost geëmailleerd (niet-standaard RAL)", help: "Eenmalig per kleur per order." },
  { key: "bengSampleCents", label: "BENGglas-staal", help: "Prijs van een monster vacuümglas." },
  { key: "bengHybrideGelaagdCents", label: "BENGglas hybride gelaagd (per m²)", help: "Hybride opbouw met gelaagde tegenruit (2× EVA)." },
];

/** Onbruikbare of ontbrekende waarden vervangen door de laatst bekende juiste. */
export function coerceBewerkingsTarieven(raw: Record<string, unknown> | undefined): BewerkingsTarieven {
  const out = { ...DEFAULT_BEWERKINGSTARIEVEN };
  if (!raw) return out;
  for (const k of Object.keys(out) as (keyof BewerkingsTarieven)[]) {
    const v = Number(raw[k]);
    // Bovengrens als typefout-vangnet: een tarief van een factor 100 te hoog zou elke ruit
    // onbetaalbaar maken zonder dat iemand het merkt.
    if (Number.isFinite(v) && v >= 0 && v <= 1_000_000) out[k] = v;
  }
  return out;
}
